<template>
  <v-container fluid style="height: 95vh; background-color: #ffffff">
    <v-row style="height: 90%; background-color: #ffffff" class="d-flex">
      <v-col cols="12" md="3">
        <v-select
          v-model="selectedSeason"
          class="pa-2"
          outlined
          filled
          dense
          hide-details
          label="Season"
          :items="ranchSeasonList"
          item-text="SID"
          return-object
          color="teal darken-3"
          :loading="datasetLoading"
        >
        </v-select>

        <template v-if="$vuetify.breakpoint.mdAndUp">
          <v-list style="overflow-y: auto" class="px-2">
            <v-list-item-group
              v-model="selectedTable"
              mandatory
              color="teal darken-3"
            >
              <v-list-item
                v-for="(item, index) in tableDatasets.map((item) =>
                  formatTableName(item.DatasetName),
                )"
                :key="index"
                :value="item"
                :loading="datasetLoading"
                dense
              >
                <v-list-item-content>
                  <v-list-item-title v-text="item"></v-list-item-title>
                </v-list-item-content>
              </v-list-item>
            </v-list-item-group>
          </v-list>
        </template>
        <template v-else>
          <v-select
            v-model="selectedTable"
            class="pa-2"
            outlined
            filled
            dense
            hide-details
            :items="
              tableDatasets.map((item) => formatTableName(item.DatasetName))
            "
            label="Table"
            color="teal darken-3"
            :loading="datasetLoading"
          >
          </v-select>
        </template>
      </v-col>
      <v-col cols="12" md="9" style="height: 100%">
        <data-table-alternate
          v-if="activeTable"
          :key="activeTable.FileId"
          :table-information="activeTable"
          :table-dataset="tableDataset"
        >
        </data-table-alternate>
        <v-skeleton-loader v-else type="table"></v-skeleton-loader>
      </v-col>
    </v-row>
  </v-container>
</template>

<script>
import DataTableAlternate from "@/components/tables/DataTableAlternate.vue";
import { getDataset, getTable } from "@/api/v2";

export default {
  name: "RanchTableManager",

  components: { DataTableAlternate },

  data() {
    return {
      selectedSeason: null,
      datasetLoading: false,
      selectedTable: null,
      activeTable: null,
      tableUpdateStatus: null,
      tableDataset: null,
      // hide tables that are not relevant to the season type, but keep the order consistent.
      // during open season, hide supplementation.
      openSeasonPreferredTableOrder: [
        "Herd - Chronology",
        "Herd - Size",
        "Grazing cell - Herds",
        "Grazing cell - Paddocks",
        "Forage - Carryover",
        "Planning - Rating",
        "Planning - Recovery time",
        "Planning - Paddock concerns",
        "Vegetation Data",
        "Rainfall Data",
      ],

      // during closed season, hide recovery time, rating, data collection tables.
      closedSeasonPreferredTableOrder: [
        "Herd - Chronology",
        "Herd - Size",
        "Grazing cell - Herds",
        "Grazing cell - Paddocks",
        "Forage - Carryover",
        "Forage - Supplementation",
        "Planning - Paddock concerns",
        "Planning - Parameters",
      ],
    };
  },

  watch: {
    selectedTable(table) {
      if (!table) return;

      const selectedTableDataset = this.tableDatasets.find((item) =>
        item.DatasetName.includes(table),
      );

      this.fetchTableDataset(selectedTableDataset);
    },

    selectedSeason() {
      if (!this.selectedTable) return;

      const selectedTableDataset = this.tableDatasets.find((item) =>
        item.DatasetName.includes(this.selectedTable),
      );

      this.fetchTableDataset(selectedTableDataset);
    },
  },

  computed: {
    ranchSeasonList() {
      const ranchSeasonList = this.$store.state.accountData.seasonList.seasons;
      return ranchSeasonList.sort((a, b) => b.SID.localeCompare(a.SID));
    },

    datasetInformation() {
      return this.$store.state.mapping.singleDataset;
    },

    ranchName() {
      return this.$store.state.mapping.singleDataset
        ? this.$store.state.mapping.singleDataset.DatasetName
        : null;
    },

    tableDatasets() {
      const filteredDatasets =
        this.$store.state.accountData.datasetList.datasets.AllDatasets.filter(
          (dataset) =>
            dataset.Tags.includes(this.ranchName) &&
            dataset.DatasetType === "Table" &&
            !dataset.DatasetName.includes("Season"),
        );

      const preferredTableOrder =
        this.selectedSeason["Season type"] === "Growing" ||
        this.selectedSeason["Season type"] === "Open"
          ? this.openSeasonPreferredTableOrder
          : this.selectedSeason["Season type"] === "Closed" ||
            this.selectedSeason["Season type"] === "Dormant"
          ? this.closedSeasonPreferredTableOrder
          : [];

      return preferredTableOrder
        .map((preferredTableName) =>
          filteredDatasets.find((d) =>
            d.DatasetName.includes(preferredTableName),
          ),
        )
        .filter(Boolean);
    },
  },

  methods: {
    formatTableName(datasetName) {
      return datasetName.split("/")[1];
    },

    async fetchTableDataset(selectedTableDataset) {
      const tableDataset = await getDataset(
        selectedTableDataset.DatasetName,
        selectedTableDataset.DatasetOwner,
      );

      this.tableDataset = tableDataset;

      const findFile = (files, startDate) =>
        files.find((file) => file.StartDate === startDate);

      const tableFile = findFile(
        tableDataset.Files,
        this.selectedSeason["Season start date*"],
      );

      if (!tableFile) {
        this.$showAlert({
          text: "No table file found for the selected season",
          type: "error",
        });
        return;
      }

      const response = await getTable(
        tableDataset.DatasetName,
        tableDataset.DatasetOwner,
        tableFile.Files[0].TablePK,
        false,
      );

      this.activeTable = response;
    },
  },

  created() {
    if (this.ranchSeasonList.length === 0) {
      return;
    }

    this.selectedSeason = this.ranchSeasonList[0];
  },
};
</script>

<style lang="scss" scoped>
::v-deep tbody {
  tr.v-data-table__selected {
    background-color: #f8f4e3 !important;
  }
}

::v-deep tbody {
  tr:hover {
    background-color: #f8f4e3 !important;
  }
}
::v-deep .v-data-table-header {
  background-color: #ececec;
  // background-color: #f8f4e3;
}

.aside-right {
  border-left: 2px solid black;
}

.aside-small {
  border-left: none;
}
</style>
